import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { isNil, pathOr } from 'ramda';
import ReactMarkdown from 'react-markdown';
import ReactBnbGallery from 'react-bnb-gallery';
import ReactPixel from 'react-facebook-pixel';

import { optional, isBoardgameAvailable } from '../utilities';
import galleryPhrases from '../utilities/galleryPhrases';

import BoardgameHeader from './BoardgameHeader';
import Tag from './Tag';
import ExternalLink from './ExternalLink';
import Page from './Page';
import Players from './Players';
import PlayTime from './PlayTime';
import IconSadFace from './IconSadFace';
import Error from './Error';
import Loader from './Loader';
import GalleryImage from './GalleryImage';

import placeIcon from '../assets/images/icon-plane.svg';
import '../sass/BoardgamePage.sass';

class BoardgamePage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      requested: false,
      galleryRequested: false,
      page: 1,
      limit: 24,
      galleryOpened: false,
      selectedImageIndex: undefined,
    };

    this.notFound = this.notFound.bind(this);
    this.pageTitle = this.pageTitle.bind(this);
    this.loadGalleryNextPage = this.loadGalleryNextPage.bind(this);
    this.hasMorePage = this.hasMorePage.bind(this);
    this.openGallery = this.openGallery.bind(this);
    this.closeGallery = this.closeGallery.bind(this);

  }

  componentDidMount() {
    ReactPixel.pageView();
    this.props.changeHeaderType('transparent');
    
    if (!this.state.requested && !this.props.boardgame) {
      this.props.getCategories();
      this.props.getBoardgame(this.props.params.id);
      this.setState({ requested: true });
    }

    if (this.props.boardgame && !this.state.galleryRequested) {
      this.props.getGalleryForBoardgame(this.props.boardgame.id, this.props.boardgame.bggId, this.state.page, this.state.limit);
      this.setState({ galleryRequested: true });
    }
  }

  componentDidUpdate() {
    if (this.props.boardgame && !this.state.galleryRequested) {
      this.props.getGalleryForBoardgame(this.props.boardgame.id, this.props.boardgame.bggId, this.state.page, this.state.limit);
      this.setState({ galleryRequested: true });
    }
  }

  componentWillUnmount() {
    this.props.changeHeaderType('default');
  }

  notFound() {
    return !this.props.loading && this.state.requested && isNil(this.props.boardgame);
  }

  pageTitle() {
    let partialTitle;
    if (this.props.loading) {
      partialTitle = 'Caricamento...';
    } else if (this.notFound()) {
      partialTitle = 'Non trovato';
    } else {
      partialTitle = this.props.boardgame && this.props.boardgame.title;
    }

    return `${partialTitle} - Smart Gaming`;
  }

  loadGalleryNextPage() {
    this.setState(prevState => {
      return { page: prevState.page + 1 };
    }, () => {
      this.props.getGalleryForBoardgame(
        this.props.boardgame.id,
        this.props.boardgame.bggId,
        this.state.page,
        this.state.limit
      );
    });
  }

  hasMorePage() {
    return this.state.page < pathOr(null, ['galleryConfiguration', 'endpage'], this.props);
  }

  openGallery(index) {
    return () => {
      this.setState({
        galleryOpened: true, 
        selectedImageIndex: index,
      });
    };
  }

  closeGallery() {
    this.setState({
      galleryOpened: false, 
      selectedImageIndex: undefined,
    });
  }

  render() {
    if (this.notFound()) {
      this.props.changeHeaderType('default');
      return (
        <Error
          title="Non abbiamo trovato il gioco che hai chiesto..."
          message="Forse ti sei perso mentre sfogliavi i regolamenti dei tuoi nuovi giochi."
          icon={placeIcon} />
      );
    }

    return (
      <React.Fragment>
        <Helmet>
          <title>{ this.pageTitle() }</title>
        </Helmet>
        { 
          this.props.boardgame && 
            <Page heroComponent={ () => <BoardgameHeader title={this.props.boardgame.title} imageUrl={this.props.boardgame.image} /> } paddingTop={false}>
              <main className="sg-boardgame-page py-4 px-4 md:px-8">
                {
                  !isBoardgameAvailable(this.props.boardgame.availableCopies, this.props.boardgame.reservedCopies) &&
                    <div className="flex flex-col items-center relative bg-yellow-400 p-4 text-center font-bold mb-8 rounded-lg shadow-lg">
                      <IconSadFace className="w-10 mb-2" />
                      Il gioco è momentaneamente occupato. Avvisaci e te lo riserveremo!
                    </div>
                }
                <div className="relative flex flex-row justify-center mt-2">
                  <div className="pl-6 pr-2 mr-2">
                    <PlayTime minPlayTime={this.props.boardgame.minPlayTime} maxPlayTime={this.props.boardgame.maxPlayTime} iconSize={22} />
                  </div>
                  <div className="pl-2 pr-6">
                    <Players minPlayers={this.props.boardgame.minPlayers} maxPlayers={this.props.boardgame.maxPlayers} iconSize={22} />
                  </div>
                </div>
                <div className="sg-boardgame-page__description relative mt-4">
                  <ReactMarkdown>{this.props.boardgame.description}</ReactMarkdown>
                </div>
                { 
                  optional(this.props.boardgame.articles, []).length > 0 && 
                    <div className="relative mt-8">
                      <h1 className="text-xl font-serif font-black mb-2">Per saperne di più</h1>
                      <div className="flex flex-col justify-between">
                        { 
                          this.props.boardgame.articles
                          .map((article, index) => 
                            <ExternalLink key={index} href={article.url} target="_blank" className="mb-2">{ article.label }</ExternalLink>
                          )
                        }
                      </div>
                    </div>
                }
                {
                  optional(this.props.boardgame.categories, []).length > 0 &&
                    <div className="relative flex flex-wrap mt-8">
                      { 
                        this.props.boardgame.categories.map(category =>
                          <Tag key={category.id} text={category.name} />
                        )
                      }
                    </div>
                }

                {
                  this.state.galleryRequested && optional(this.props.gallery, []).length > 0 && 
                    <section>
                      <h1 className="text-xl font-serif font-black mt-8 mb-1">Galleria</h1>
                      <h2 className="font-sans font-light mb-2">
                        Credits <a rel="noopener noreferrer" target="_blank" href="https://www.boardgamegeek.com" className="font-medium">BoardGameGeek.com &trade;</a>
                      </h2>
                      <div className="flex flex-wrap">
                        {
                          this.props.gallery.map((image, index) =>
                            <GalleryImage key={image.id} image={image} onClick={this.openGallery(index)} />
                          )
                        }
                      </div>
                      { 
                        this.hasMorePage() &&
                          <div className="flex justify-center mt-3 md:mt-6">
                            {
                              this.props.loadingGallery
                                ? <Loader />
                                : <button onClick={this.loadGalleryNextPage}
                                          className="appearance-none border border-gray-600 bg-white hover:opacity-50 text-gray-600 flex justify-center items-center rounded-full px-4 py-1 w-full md:w-48 md:mr-4">
                                    Carica altre immagini
                                  </button>
                            }
                          </div>
                      }
                      <ReactBnbGallery
                        show={this.state.galleryOpened}
                        photos={this.props.gallery}
                        activePhotoIndex={this.state.selectedImageIndex}
                        onClose={this.closeGallery}
                        phrases={galleryPhrases} />
                    </section>
                }
              </main>
            </Page>
        }
      </React.Fragment>
    );
  }
}

export default BoardgamePage;